import {
	createAtomNameFromOptions,
	createMapItemAtomName,
	createMapKeyAtomName,
	createMapSizeAtomName,
	createMapValueAtomName,
	NameOptions,
} from "../extras/reactive-names";
import { afterChange, beforeChange } from "../extras/reactive-plugin";
import {
	getMapClearChanges,
	getMapDeleteChanges,
	getMapSetChanges,
} from "../extras/reactive-plugin-map";
import { runTransaction } from "../primitives/reactive-action";
import { createAtom } from "../primitives/reactive-atom";
import { getReactiveLayer } from "../primitives/reactive-layer";
import { reactiveValueEquals } from "../primitives/reactive-options";
import { Atom } from "../primitives/reactive-primitive-types";
import { isTracking } from "../primitives/reactive-tracking";
import { ContextReactiveOptions } from "./reactive-abstraction-types";
import { getChild, getChildOptions } from "./reactive-children";
import { createProxy, ensureInert, getReactive } from "./reactive-proxy";

type ProxyTarget = Map<unknown, unknown>;

type Reactive = {
	items: Map<unknown, Atom>;
	size: Atom;
};

export const createReactiveMap = <T extends Map<unknown, unknown>>(
	inertMap: T,
	reactiveOptions?: ContextReactiveOptions,
	nameOptions?: NameOptions
): T => {
	const atomName = createAtomNameFromOptions(nameOptions);

	const proxy = new Proxy(
		inertMap,
		new ReactiveMapProxyHandler(
			new ReactiveMapOverrides(
				atomName,
				inertMap,
				{
					items: new Map(),
					size: createAtom(createMapSizeAtomName(atomName)),
				},
				() => proxy,
				reactiveOptions
			)
		)
	) as T;

	return proxy;
};

class ReactiveMapProxyHandler implements ProxyHandler<ProxyTarget> {
	private mapOverrides: ReactiveMapOverrides;

	constructor(mapOverrides: ReactiveMapOverrides) {
		this.mapOverrides = mapOverrides;
	}

	get(inertMap: ProxyTarget, property: string | symbol, receiver: unknown) {
		const ownPropertyDescriptor = Reflect.getOwnPropertyDescriptor(
			inertMap,
			property
		);

		const isInherited =
			Reflect.has(inertMap, property) && !ownPropertyDescriptor;

		if (isInherited && property in this.mapOverrides) {
			return this.mapOverrides[property as keyof ReactiveMapOverrides];
		}

		return Reflect.get(inertMap, property, receiver);
	}
}

class ReactiveMapOverrides implements Map<unknown, unknown> {
	#atomName: string;
	#inertMap: Map<unknown, unknown>;
	#reactive: Reactive;
	#getProxy: () => Map<unknown, unknown>;
	#options: { reactiveOptions?: ContextReactiveOptions };

	constructor(
		atomName: string,
		inertMap: Map<unknown, unknown>,
		reactive: Reactive,
		getProxy: () => Map<unknown, unknown>,
		reactiveOptions?: ContextReactiveOptions
	) {
		this.#atomName = atomName;
		this.#inertMap = inertMap;
		this.#reactive = reactive;
		this.#getProxy = getProxy;
		this.#options = { reactiveOptions };
	}

	[Symbol.iterator] = () => {
		return this.#getMapEntries();
	};

	entries = () => {
		return this.#getMapEntries();
	};

	keys = () => {
		return this.#keys();
	};

	values = () => {
		return this.#values();
	};

	get [Symbol.toStringTag]() {
		return Map.prototype[Symbol.toStringTag];
	}

	get size(): number {
		this.#reactive.size.reportObserved();
		return this.#inertMap.size;
	}

	has = (key: unknown): boolean => {
		const storedKey = this.#getStored(key);
		const result = this.#hasStoredKey(storedKey);
		this.#readItem(storedKey);
		return result;
	};

	get = (key: unknown): unknown => {
		const storedKey = this.#getStored(key);
		const result = this.#getStoredValueByKey(storedKey);
		this.#readItem(storedKey);
		return getChild(this.#options, result, () =>
			createProxy(result, getChildOptions(this.#options), {
				parentAtomName: this.#atomName,
				createAtomName: createMapValueAtomName,
				childIndex: this.#getChildIndex(storedKey),
			})
		);
	};

	set = (key: unknown, value: unknown) => {
		const storedKey = this.#getStored(key);
		const storedValue = this.#getStored(value);
		const itemAlreadyExists = this.#hasStoredKey(storedKey);

		const currentStoredValue = this.#getStoredValueByKey(storedKey);
		const isValueChanged = !reactiveValueEquals(
			currentStoredValue,
			storedValue
		);

		const changes =
			!this.#hasPlugins() || (itemAlreadyExists && !isValueChanged)
				? null
				: getMapSetChanges(
						this.#getProxy(),
						storedKey,
						!itemAlreadyExists,
						currentStoredValue,
						storedValue
				  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		Map.prototype.set.call(this.#inertMap, storedKey, storedValue);
		runTransaction(() => {
			if (!itemAlreadyExists || isValueChanged) {
				this.#notifyItem(storedKey);
			}
			if (!itemAlreadyExists) {
				this.#reactive.size.reportChanged();
			}
		});

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return this;
	};

	delete = (key: unknown): boolean => {
		const storedKey = this.#getStored(key);
		const itemAlreadyExists = this.#hasStoredKey(storedKey);

		const changes =
			!this.#hasPlugins() || !itemAlreadyExists
				? null
				: getMapDeleteChanges(
						this.#getProxy(),
						storedKey,
						this.#getStoredValueByKey(storedKey)
				  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		const resultStored = Map.prototype.delete.call(this.#inertMap, storedKey);
		const reactive = getReactive(storedKey as object);
		const resultReactive = !!(
			reactive && Map.prototype.delete.call(this.#inertMap, reactive)
		);
		const result = resultStored || resultReactive;
		if (result) {
			runTransaction(() => {
				this.#notifyItem(storedKey);
				this.#reactive.size.reportChanged();
			});
		}

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}

		return result;
	};

	clear = (): void => {
		const isAlreadyEmpty = this.#inertMap.size === 0;

		const changes =
			!this.#hasPlugins() || isAlreadyEmpty
				? null
				: getMapClearChanges(
						this.#getProxy(),
						Array.from(this.#inertMap.entries())
				  );

		if (changes) {
			beforeChange(changes, this.#options.reactiveOptions?.plugins);
		}

		runTransaction(() => {
			Map.prototype.forEach.call(
				this.#inertMap,
				(_value: unknown, key: unknown) => {
					Map.prototype.delete.call(this.#inertMap, key);
					this.#notifyItem(key);
				}
			);
			if (!isAlreadyEmpty) {
				this.#reactive.size.reportChanged();
			}
		});

		if (changes) {
			afterChange(changes, this.#options.reactiveOptions?.plugins);
		}
	};

	forEach = (
		callbackfn: (
			value: unknown,
			key: unknown,
			map: Map<unknown, unknown>
		) => void,
		thisArg?: any
	) => {
		this.#reactive.size.reportObserved();
		const result = Map.prototype.forEach.call(
			this.#inertMap,
			(value: unknown, key: unknown, map: Map<unknown, unknown>) => {
				this.#readItem(key);
				const childIndex = this.#getChildIndex(key);
				return callbackfn.call(
					this,
					getChild(this.#options, value, () =>
						createProxy(value, getChildOptions(this.#options), {
							parentAtomName: this.#atomName,
							createAtomName: createMapValueAtomName,
							childIndex,
						})
					),
					getChild(this.#options, key, () =>
						createProxy(key, getChildOptions(this.#options), {
							parentAtomName: this.#atomName,
							createAtomName: createMapKeyAtomName,
							childIndex,
						})
					),
					map
				);
			},
			thisArg
		);
		return result;
	};

	*#keys(): MapIterator<unknown> {
		const result: IterableIterator<[unknown, unknown]> =
			Map.prototype.entries.call(this.#inertMap);
		this.#reactive.size.reportObserved();
		for (const [key] of result) {
			this.#readItem(key);
			yield getChild(this.#options, key, () =>
				createProxy(key, getChildOptions(this.#options), {
					parentAtomName: this.#atomName,
					createAtomName: createMapKeyAtomName,
					childIndex: this.#getChildIndex(key),
				})
			);
		}
	}

	*#values(): MapIterator<unknown> {
		const result: IterableIterator<[unknown, unknown]> =
			Map.prototype.entries.call(this.#inertMap);
		this.#reactive.size.reportObserved();
		for (const [key, value] of result) {
			this.#readItem(key);
			yield getChild(this.#options, value, () =>
				createProxy(value, getChildOptions(this.#options), {
					parentAtomName: this.#atomName,
					createAtomName: createMapValueAtomName,
					childIndex: this.#getChildIndex(key),
				})
			);
		}
	}

	#getStored(value: unknown) {
		return this.#options.reactiveOptions?.deep === false
			? value
			: ensureInert(value);
	}

	#getStoredValueByKey(storedKey: unknown) {
		const hasStoredValueDirectly = Map.prototype.has.call(
			this.#inertMap,
			storedKey
		);
		if (
			hasStoredValueDirectly ||
			this.#options.reactiveOptions?.deep === false
		) {
			return Map.prototype.get.call(this.#inertMap, storedKey);
		}

		const reactiveKey = getReactive(storedKey as object);
		return reactiveKey
			? Map.prototype.get.call(this.#inertMap, reactiveKey)
			: undefined;
	}

	#hasStoredKey(storedKey: unknown) {
		const hasStoredValueDirectly = Map.prototype.has.call(
			this.#inertMap,
			storedKey
		);
		if (
			hasStoredValueDirectly ||
			this.#options.reactiveOptions?.deep === false
		) {
			return hasStoredValueDirectly;
		}

		const reactiveKey = getReactive(storedKey as object);
		return (
			(reactiveKey && Map.prototype.has.call(this.#inertMap, reactiveKey)) ||
			false
		);
	}

	#cleanupItem(key: unknown) {
		this.#reactive.items.delete(key);
	}

	#notifyItem(key: unknown) {
		const atom = this.#reactive.items.get(key);
		if (atom) {
			atom.reportChanged();
		}
	}

	#getChildIndex(key: unknown) {
		switch (typeof key) {
			case "string":
			case "number":
			case "bigint":
			case "boolean":
			case "symbol":
			case "undefined":
				return String(key);

			default:
				return undefined;
		}
	}

	#readItem(key: unknown) {
		let atom = this.#reactive.items.get(key);
		if (!atom) {
			if (!isTracking()) {
				return;
			}
			atom = getReactiveLayer().createAtom(
				createMapItemAtomName(this.#atomName, this.#getChildIndex(key)),
				{
					onBecomeUnobservedListener: () => {
						this.#cleanupItem(key);
					},
				}
			);
			this.#reactive.items.set(key, atom);
		}
		atom.reportObserved();
	}

	*#getMapEntries(): MapIterator<[unknown, unknown]> {
		const result: IterableIterator<[unknown, unknown]> =
			Map.prototype.entries.call(this.#inertMap);
		this.#reactive.size.reportObserved();
		for (const [key, value] of result) {
			this.#readItem(key);
			const childIndex = this.#getChildIndex(key);
			yield [
				getChild(this.#options, key, () =>
					createProxy(key, getChildOptions(this.#options), {
						parentAtomName: this.#atomName,
						createAtomName: createMapKeyAtomName,
						childIndex,
					})
				),
				getChild(this.#options, value, () =>
					createProxy(value, getChildOptions(this.#options), {
						parentAtomName: this.#atomName,
						createAtomName: createMapValueAtomName,
						childIndex,
					})
				),
			];
		}
	}

	#hasPlugins() {
		return (this.#options.reactiveOptions?.plugins?.length ?? 0) > 0;
	}
}
